/**
 * @fileoverview Standard CRUD scenario detector for CopilotBar.
 *
 * Handles CopilotBar configuration when entity CRUD operations are
 * performed in the standard side panel view (not in a modal).
 *
 * @module CopilotBar/scenarios/crud/standardCrud
 */

import { COPILOT_BAR_SCENARIOS } from '../constants';

import { buildCrudConfig, extractCrudContext } from './crudHelpers';

/**
 * Scenario detector for standard CRUD panel operations.
 *
 * This scenario is activated when:
 * - The entity CRUD panel is open (`entityCrudState.isOpen === true`)
 * - There is an active CRUD entity (`entityCrudState.activeCrudName` is truthy)
 * - The CRUD is NOT displayed as a modal (`crudData.isModal === false`)
 *
 * When active, the CopilotBar is displayed as a side panel with:
 * - Width matching the entity's configured `crudPanelWidth`
 * - Pinned to the right side of the viewport
 *
 * @type {Object}
 * @property {string} scenario - The scenario type identifier (STANDARD_CRUD)
 * @property {Function} condition - Evaluates if a standard CRUD panel is open
 * @property {Function} buildConfig - Builds the CopilotBar configuration for panel context
 *
 * @example
 * // Condition evaluation
 * standardCrudScenario.condition({
 *     entityCrudState: {
 *         isOpen: true,
 *         activeCrudName: 'accounts-123',
 *         entityCrud: { 'accounts-123': { isModal: false } }
 *     }
 * });
 * // Returns: true
 */
export const standardCrudScenario = {
    buildConfig: ({ dispatch, getEntityCrudState, setCopilotBarProps, state }) => {
        const { entityCrudState } = state;
        const context = extractCrudContext(
            entityCrudState.activeCrudName,
            entityCrudState.entityCrud,
        );

        if (!context) return null;

        return buildCrudConfig({
            context,
            dispatch,
            getEntityCrudState,
            pinnedToRight: true,
            setCopilotBarProps,
            width: context.entity?.crudPanelWidth,
        });
    },
    condition: ({ entityCrudState }) => {
        const entityCrudActive = entityCrudState.activeCrudName;
        if (!entityCrudState.isOpen || !entityCrudActive) return false;
        const crudData = entityCrudState.entityCrud[entityCrudActive];
        return crudData && !crudData.isModal;
    },
    scenario: COPILOT_BAR_SCENARIOS.STANDARD_CRUD,
};
